import React from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { CertificateListItem } from '../types';
import { countryLabel } from './CountrySelect';
import BusinessUnitChip from './BusinessUnitChip';
import MetalStatusBadge from './MetalStatusBadge';
import ZollIncompleteMark, { zollProofIncomplete } from './ZollIncompleteMark';
import SortableHeader from './SortableHeader';
import type { SortDirection } from '../utils/tableSort';
import { useVisibleRange } from '../utils/useVisibleRange';

export type CertificateSortColumn =
  | 'certificate_number'
  | 'lieferant'
  | 'certificate_date'
  | 'heat_number'
  | 'melt_country'
  | 'melt_works'
  | 'country'
  | 'material_grade'
  | 'surface'
  | 'business_unit';

interface CertificateTableProps {
  certificates: CertificateListItem[];
  sort: CertificateSortColumn;
  order: SortDirection;
  onSort: (column: CertificateSortColumn) => void;
  onDelete: (certificate: CertificateListItem) => void;
  onDownloadPdf: (certificateId: number) => void;
  canEditCertificate: (certificate: CertificateListItem) => boolean;
}

const COLUMN_COUNT = 12;
const VIRTUAL_AFTER = 40;
const INITIAL_ROW_HEIGHT = 41;

const CertificateTable: React.FC<CertificateTableProps> = ({
  certificates,
  sort,
  order,
  onSort,
  onDelete,
  onDownloadPdf,
  canEditCertificate,
}) => {
  const navigate = useNavigate();
  const bodyRef = React.useRef<HTMLTableSectionElement>(null);
  const [rowHeight, setRowHeight] = React.useState(INITIAL_ROW_HEIGHT);
  const virtual = certificates.length > VIRTUAL_AFTER;
  const range = useVisibleRange(virtual ? certificates.length : 0, rowHeight, bodyRef);
  const start = virtual ? range.start : 0;
  const end = virtual ? range.end : certificates.length;
  const visible = virtual ? certificates.slice(start, end) : certificates;

  const measureRow = (node: HTMLTableRowElement | null) => {
    if (!node || !virtual) return;
    const height = Math.round(node.getBoundingClientRect().height);
    if (height > 20 && Math.abs(height - rowHeight) > 1) setRowHeight(height);
  };

  return (
    <TableContainer component={Paper}>
      <Table stickyHeader size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
        <TableHead>
          <TableRow>
            <SortableHeader column="certificate_number" label="Zertifikat" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="lieferant" label="Lieferant" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="certificate_date" label="Datum" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="heat_number" label="Schmelz-Nr." sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="melt_country" label="Schmelzland" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="melt_works" label="Schmelzwerk" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="country" label="Ursprung" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="material_grade" label="Güte" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="surface" label="Oberfläche" sort={sort} order={order} onSort={onSort} />
            <SortableHeader column="business_unit" label="Bereich" sort={sort} order={order} onSort={onSort} />
            <TableCell>Metallwerte</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody ref={bodyRef}>
          {virtual && start > 0 && (
            <TableRow aria-hidden>
              <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 0, lineHeight: 0 }}>
                <div style={{ height: start * rowHeight }} />
              </TableCell>
            </TableRow>
          )}
          {visible.map((certificate, index) => (
            <TableRow
              key={certificate.id}
              ref={index === 0 ? measureRow : undefined}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/certificates/${certificate.id}`)}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {certificate.certificate_number}
                  </Typography>
                  {certificate.has_pdf && (
                    <Tooltip title="PDF verfügbar">
                      <PictureAsPdfIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    </Tooltip>
                  )}
                  {certificate.blocked && (
                    <Typography variant="body2" color="warning.main" noWrap sx={{ fontSize: 12 }}>
                      Unvollständig
                    </Typography>
                  )}
                  {zollProofIncomplete(certificate) && <ZollIncompleteMark />}
                </Box>
              </TableCell>
              <TableCell>{certificate.lieferant_name ?? '-'}</TableCell>
              <TableCell>{dayjs(certificate.certificate_date).format('DD.MM.YYYY')}</TableCell>
              <TableCell>{certificate.heat_number || '—'}</TableCell>
              <TableCell>
                {certificate.melt_country_name
                  ? countryLabel(
                      {
                        name: certificate.melt_country_name,
                        code: certificate.melt_country_code || '',
                        melt_pour_code: certificate.melt_pour_code,
                        id: 0,
                        created_at: '',
                      },
                      true
                    )
                  : '—'}
              </TableCell>
              <TableCell>{certificate.melt_works || '—'}</TableCell>
              <TableCell>
                {certificate.country_name
                  ? countryLabel({ name: certificate.country_name, code: certificate.country_code || '', id: 0, created_at: '' })
                  : '—'}
              </TableCell>
              <TableCell>{certificate.material_grade_name}</TableCell>
              <TableCell>{certificate.surface_name ?? '—'}</TableCell>
              <TableCell>
                <BusinessUnitChip unit={certificate.business_unit} />
              </TableCell>
              <TableCell>
                <MetalStatusBadge
                  metalValues={certificate.metal_values || []}
                  materialGradeCompositions={certificate.grade_compositions || []}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                  {certificate.has_pdf && (
                    <IconButton size="small" onClick={() => onDownloadPdf(certificate.id)} title="PDF herunterladen">
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/certificates/${certificate.id}`)}
                    title="Anzeigen"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  {canEditCertificate(certificate) && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/certificates/${certificate.id}`)}
                        title="Bearbeiten"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDelete(certificate)} title="Löschen" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {virtual && end < certificates.length && (
            <TableRow aria-hidden>
              <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 0, lineHeight: 0 }}>
                <div style={{ height: (certificates.length - end) * rowHeight }} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CertificateTable;
